/**
 * Stripe API service for Google Pay integration
 */

export class GooglePayStripeService {
  constructor() {
    this.stripe = null;
    this.elements = null;
    this.buttonElement = null;
  }

  /**
   * Wait for Stripe library to be loaded
   * @returns {Promise<void>}
   */
  async waitForStripe() {
    return new Promise((resolve) => {
      const checkStripe = () => {
        if (typeof Stripe !== 'undefined') {
          resolve();
        } else {
          // retry in 100ms
          setTimeout(checkStripe, 100);
        }
      };
      checkStripe();
    });
  }

  /**
   * Get Stripe public key from server
   * @returns {Promise<Object|null>}
   */
  async getStripeKey() {
    // build URL with all js_query_params
    let url = `/wallet-key?method=google_pay&product_id=${js_data.product.id}`;
    
    // add all js_query_params to the URL
    for (let key of Object.keys(js_query_params)) {
      url += `&${encodeURIComponent(key)}=${encodeURIComponent(js_query_params[key])}`;
    }

    try {
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch Stripe key:', error);
      return null;
    }
  }

  /**
   * Initialize Stripe instance
   * @param {string} key - stripe public key
   * @returns {boolean} - success status
   */
  initializeStripe(key) {
    if (typeof Stripe === 'undefined') {
      console.error('Stripe is not loaded');
      return false;
    }

    this.stripe = Stripe(key);
    return true;
  }

  /**
   * Create Stripe elements instance
   * @param {number} amount - payment amount in cents
   * @param {string} currency - payment currency
   * @returns {boolean} - success status
   */
  createElements(amount, currency) {
    if (!this.stripe) {
      console.error('Stripe not initialized');
      return false;
    }

    this.elements = this.stripe.elements({
      mode: 'payment',
      amount: amount,
      currency: currency.toLowerCase(),
    });

    return true;
  }

  /**
   * Create Express Checkout Element for Google Pay
   * @param {Object} options - element configuration options
   * @returns {Object|null} - button element or null on error
   */
  createExpressCheckoutElement(options) {
    if (!this.elements) {
      console.error('Stripe elements not created');
      return null;
    }

    this.buttonElement = this.elements.create('expressCheckout', options);
    return this.buttonElement;
  }

  /**
   * Mount Google Pay button to DOM element
   * @param {string} selector - CSS selector for mount target
   * @returns {boolean} - success status
   */
  mountButton(selector) {
    if (!this.buttonElement) {
      console.error('Button element not created');
      return false;
    }

    // check if target element exists before mounting
    const targetElement = document.querySelector(selector);
    if (!targetElement) {
      console.error(`Target element ${selector} not found in DOM`);
      return false;
    }

    // check if element is visible (not hidden by v-if or v-show)
    if (targetElement.offsetParent === null) {
      console.error(`Target element ${selector} exists but is not visible`);
      return false;
    }

    try {
      this.buttonElement.mount(selector);
      return true;
    } catch (error) {
      console.error('Failed to mount button:', error);
      return false;
    }
  }

  /**
   * Confirm payment with Stripe
   * @param {string} clientSecret - payment intent client secret
   * @param {string} returnUrl - return URL after payment
   * @returns {Promise<Object>} - payment confirmation result
   */
  async confirmPayment(clientSecret, returnUrl) {
    if (!this.stripe || !this.elements) {
      throw new Error('Stripe not properly initialized');
    }

    return await this.stripe.confirmPayment({
      elements: this.elements,
      clientSecret: clientSecret,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required'
    });
  }

  /**
   * Update payment amount in elements
   * @param {number} amount - new amount in cents
   */
  updateAmount(amount) {
    if (this.elements && amount) {
      this.elements.update({
        amount: amount
      });
    }
  }

  /**
   * Add event listener to button element
   * @param {string} event - event name
   * @param {Function} handler - event handler function
   */
  addEventListener(event, handler) {
    if (this.buttonElement) {
      this.buttonElement.on(event, handler);
    }
  }

  /**
   * Destroy Stripe elements and cleanup
   */
  destroy() {
    if (this.buttonElement) {
      this.buttonElement.destroy();
      this.buttonElement = null;
    }
    
    if (this.elements) {
      this.elements = null;
    }
    
    if (this.stripe) {
      this.stripe = null;
    }
  }
}

export default GooglePayStripeService;